export const leadVerticalOptions = [
  { value: "travel", label: "旅游 / 酒店 / 机票" },
  { value: "commerce", label: "电商 / 本地生活" },
  { value: "saas", label: "企业 SaaS / CRM / 财税" },
] as const;

export const leadRiskLevelOptions = [
  { value: "query", label: "只读查询" },
  { value: "draft", label: "创建草稿 / 提交申请" },
  { value: "write", label: "正式写操作 / 需要审批" },
] as const;

export type LeadVertical = (typeof leadVerticalOptions)[number]["value"];
export type LeadRiskLevel = (typeof leadRiskLevelOptions)[number]["value"];
export type LeadStatus = "new" | "reviewing" | "matched" | "closed";

export interface LeadSubmissionInput {
  name: string;
  vertical: LeadVertical;
  riskLevel: LeadRiskLevel;
  notes: string;
  contactName: string;
  contactHandle: string;
  website: string;
}

export interface LeadSubmissionReceipt {
  id: string;
  name: string;
  vertical: LeadVertical;
  riskLevel: LeadRiskLevel;
  notes: string;
  contactName: string;
  contactHandle: string;
  status: LeadStatus;
  createdAt: string;
}

const verticalValues = new Set<LeadVertical>(leadVerticalOptions.map(option => option.value));
const riskLevelValues = new Set<LeadRiskLevel>(leadRiskLevelOptions.map(option => option.value));

function readString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function getVerticalLabel(vertical: LeadVertical) {
  return leadVerticalOptions.find(option => option.value === vertical)?.label ?? vertical;
}

export function getRiskLevelLabel(riskLevel: LeadRiskLevel) {
  return leadRiskLevelOptions.find(option => option.value === riskLevel)?.label ?? riskLevel;
}

export function parseLeadSubmission(
  input: unknown,
): { ok: true; data: LeadSubmissionInput } | { ok: false; error: string } {
  if (!isRecord(input)) {
    return { ok: false, error: "请求格式不正确。" };
  }

  const data: LeadSubmissionInput = {
    name: readString(input.name),
    vertical: readString(input.vertical) as LeadVertical,
    riskLevel: readString(input.riskLevel) as LeadRiskLevel,
    notes: readString(input.notes),
    contactName: readString(input.contactName),
    contactHandle: readString(input.contactHandle),
    website: readString(input.website),
  };

  if (data.name.length < 3 || data.name.length > 120) {
    return { ok: false, error: "需求名称请控制在 3 到 120 个字符之间。" };
  }

  if (!verticalValues.has(data.vertical)) {
    return { ok: false, error: "请选择有效的场景。" };
  }

  if (!riskLevelValues.has(data.riskLevel)) {
    return { ok: false, error: "请选择有效的风险等级。" };
  }

  if (data.contactName.length < 2 || data.contactName.length > 80) {
    return { ok: false, error: "联系人请控制在 2 到 80 个字符之间。" };
  }

  if (data.contactHandle.length < 3 || data.contactHandle.length > 120) {
    return { ok: false, error: "联系方式请控制在 3 到 120 个字符之间。" };
  }

  if (data.notes.length < 10 || data.notes.length > 2000) {
    return { ok: false, error: "需求说明请控制在 10 到 2000 个字符之间。" };
  }

  return { ok: true, data };
}
